import React from 'react'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './context/AuthContext'
import { ToastProvider } from './components/Toast'
import Layout from './components/layout/Layout'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ClientesPage from './pages/ClientesPage'
import EquipamentosPage from './pages/EquipamentosPage'
import OrdensServicoPage from './pages/OrdensServicoPage'
import OrdemServicoSuccessPage from './pages/OrdemServicoSuccessPage'
import OrdemServicoFechamentoSuccessPage from './pages/OrdemServicoFechamentoSuccessPage'
import OrdensServicoArquivoPage from './pages/OrdensServicoArquivoPage'
import OrcamentosPage from './pages/OrcamentosPage'
import OrcamentoDetailsPage from './pages/OrcamentoDetailsPage'
import OrcamentoFormPage from './pages/OrcamentoFormPage'
import FecharOSPage from './pages/FecharOSPage'
import CuponsTermicosPage from './pages/CuponsTermicosPage'
import CupomTermicoFormPage from './pages/CupomTermicoFormPage'
import EstoquePage from './pages/EstoquePage'
import ServicosPage from './pages/ServicosPage'
import VendasPage from './pages/VendasPage'
import DashboardPage from './pages/DashboardPage'
import NotificationsPage from './pages/NotificationsPage'
import SettingsPage from './pages/SettingsPage'
import RelatoriosPage from './pages/RelatoriosPage'
import UsuariosPage from './pages/UsuariosPage'
import EstadosOSPage from './pages/configuracoes/EstadosOSPage'
import IntegracoesPage from './pages/integracoes/IntegracoesPage'
import WhatsAppPage from './pages/integracoes/WhatsAppPage'
import EmailPage from './pages/integracoes/EmailPage'
import PublicOrcamentoPage from './pages/PublicOrcamentoPage'
import './index.css'

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
    mutations: {
      retry: 1,
    },
  },
})

// Configurar router com flags futuras para suprimir warnings
const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />
  },
  {
    path: "/register",
    element: <RegisterPage />
  },
  {
    path: "/public/orcamento/:id/:token",
    element: <PublicOrcamentoPage />
  },
  {
    path: "/",
    element: <Layout />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />
      },
      {
        path: "dashboard",
        element: <DashboardPage />
      },
      {
        path: "clientes",
        element: <ClientesPage />
      },
      {
        path: "clientes/:clienteId",
        element: <ClientesPage />
      },
      {
        path: "equipamentos",
        element: <EquipamentosPage />
      },
      {
        path: "equipamentos/:equipamentoId",
        element: <EquipamentosPage />
      },
      {
        path: "ordens-servico",
        element: <OrdensServicoPage />
      },
      {
        path: "ordens-servico/:osId",
        element: <OrdensServicoPage />
      },
      {
        path: "ordens-servico/:id/success",
        element: <OrdemServicoSuccessPage />
      },
      {
        path: "ordens-servico/:id/fechamento-success",
        element: <OrdemServicoFechamentoSuccessPage />
      },
      {
        path: "ordens-servico/:id/fechar",
        element: <FecharOSPage />
      },
      {
        path: "ordens-servico/arquivo",
        element: <OrdensServicoArquivoPage />
      },
      {
        path: "orcamentos",
        element: <OrcamentosPage />
      },
      {
        path: "orcamentos/novo",
        element: <OrcamentoFormPage />
      },
      {
        path: "orcamentos/:orcamentoId",
        element: <OrcamentoDetailsPage />
      },
      {
        path: "orcamentos/:orcamentoId/editar",
        element: <OrcamentoFormPage />
      },
      {
        path: "cupons-termicos",
        element: <CuponsTermicosPage />
      },
      {
        path: "cupons-termicos/novo",
        element: <CupomTermicoFormPage />
      },
      {
        path: "cupons-termicos/:id/editar",
        element: <CupomTermicoFormPage />
      },
      {
        path: "estoque",
        element: <EstoquePage />
      },
      {
        path: "servicos",
        element: <ServicosPage />
      },
      {
        path: "vendas",
        element: <VendasPage />
      },
      {
        path: "relatorios",
        element: <RelatoriosPage />
      },
      {
        path: "notificacoes",
        element: <NotificationsPage />
      },
      {
        path: "configuracoes",
        element: <SettingsPage />
      },
      {
        path: "configuracoes/estados-os",
        element: <EstadosOSPage />
      },
      {
        path: "usuarios",
        element: <UsuariosPage />
      },
      {
        path: "integracoes",
        element: <IntegracoesPage />
      },
      {
        path: "integracoes/whatsapp",
        element: <WhatsAppPage />
      },
      {
        path: "integracoes/email",
        element: <EmailPage />
      }
    ]
  },
  {
    path: "*",
    element: <Navigate to="/dashboard" replace />
  }
], {
  future: {
    v7_relativeSplatPath: true
  }
})

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default App